import axios, { isCancel } from 'axios'
import { get, isArray } from 'lodash-es'

import {
  API_PARAM_IS_REDESIGN_CONTEXT,
  HTTP_STATUS_ACCEPTED,
  HTTP_STATUS_BAD_REQUEST,
  HTTP_STATUS_CONFLICT,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_INTERNAL_SERVER_ERROR,
  HTTP_STATUS_NOT_FOUND,
  HTTP_STATUS_PAYMENT_REQUIRED,
  HTTP_STATUS_TOO_MANY_REQUESTS,
  HTTP_STATUS_UNAUTHORIZED,
  SUCCESS_CODES,
} from '../constants/api'
import { EMPTY_OBJECT } from '../constants/common'
import {
  LOCAL_STORAGE_API_KEY,
  LOCAL_STORAGE_NAME_TOKEN,
} from '../constants/storage'

import { getAppVersion } from './appVersion'
import { isDevelopmentEnvironment } from './common'
import { getStorageItem } from './storage'

function handleError(error) {
  if (isCancel(error)) {
    return null
  }

  const status = error?.response?.status

  if (isApiUnauthorizedStatus(status)) {
    window.location = '/login'
  }

  throw error
}

/**
 * @param {ApiConfig} config
 *
 * @returns {CallApiReturn}
 */
function callApi({
  data,
  endpoint,
  headers,
  method,
  options = EMPTY_OBJECT,
  params,
}) {
  if (!endpoint) {
    throw 'WARNING! Using `callApi` without endpoint is prohibited'
  }

  return axios({
    ...options,
    ...(data ? { data } : {}),
    ...(params ? { params } : {}),
    baseURL: getOrigin(),
    headers: {
      ...headers,
      ...getHeaders(),
    },
    method,
    paramsSerializer: {
      indexes: null,
    },
    url: endpoint,
  }).catch(error => handleError(error))
}

export function getId2IdEndpoint({ outboundUuid, path }) {
  return outboundUuid ? `api/id2id/${outboundUuid}/${path}` : `api/${path}`
}

export function getHost() {
  return isDevelopmentEnvironment()
    ? `${window.location.protocol}${getStorageItem(LOCAL_STORAGE_API_KEY)}`
    : `https://${window.location.host}/`
}

export function getOrigin() {
  return isDevelopmentEnvironment()
    ? getStorageItem(LOCAL_STORAGE_API_KEY)
    : `//${window.location.host}/`
}

export function getToken() {
  return getStorageItem(LOCAL_STORAGE_NAME_TOKEN)
}

export function getHeaders() {
  const token = getToken()
  const appVersion = getAppVersion()

  return {
    'Content-Type': 'application/json',
    'X-Client-Type': 'web',
    ...(token ? { Authorization: `Token ${token}` } : undefined),
    ...(appVersion ? { 'X-App-Version': appVersion } : undefined),
  }
}

/**
 * @param {ApiConfig} config
 *
 * @returns {CallApiReturn}
 */
export function getApi(config) {
  return callApi({ ...config, method: 'get' })
}

/**
 * @param {ApiConfig} config
 *
 * @returns {CallApiReturn}
 */
export function postApi(config) {
  return callApi({ ...config, method: 'post' })
}

/**
 * @param {ApiConfig} config
 *
 * @returns {CallApiReturn}
 */
export function putApi(config) {
  return callApi({ ...config, method: 'put' })
}

/**
 * @param {ApiConfig} config
 *
 * @returns {CallApiReturn}
 */
export function patchApi(config) {
  return callApi({ ...config, method: 'patch' })
}

/**
 * @param {ApiConfig} config
 *
 * @returns {CallApiReturn}
 */
export function deleteApi(config) {
  return callApi({ ...config, method: 'delete' })
}

export function createArrayParam(paramName, items) {
  return items.map(item => [paramName, item])
}

export function createCommaSeparatedParam(items) {
  return items.join(',')
}

export function createSearchArray(paramName, items, extra) {
  let paramList = items.map(item => [paramName, item[paramName]])

  if (extra) {
    paramList = paramList.concat(extra)
  }

  return new URLSearchParams(paramList)
}

export function callFileApi({ data, endpoint, method }) {
  const token = getStorageItem(LOCAL_STORAGE_NAME_TOKEN)
  const appVersion = getAppVersion()
  const formData = new FormData()
  const query = {}

  if (data.dataUrl) {
    formData.append('origin_dataurl', data.dataUrl)
  } else {
    formData.append('origin', data.origin[0])
  }

  // Append optional fields only when set — FormData serializes a null/undefined
  // value as the literal string "null"/"undefined", which the backend rejects.
  if (data.type != null) {
    formData.append('type', data.type)
  }

  if (data.name != null) {
    formData.append('name', data.name)
  }

  if (data.parent != null) {
    formData.append('parent', data.parent)
  }

  query.body = formData

  return axios({
    baseURL: getOrigin(),
    data: query.body,
    headers: {
      ...(appVersion ? { 'X-App-Version': appVersion } : undefined),
      Authorization: `Token ${token}`,
      'X-Client-Type': 'web',
    },
    method,
    url: endpoint,
  }).then(response => {
    const status = response.status

    if (status === HTTP_STATUS_ACCEPTED) {
      const id = get(response, ['data', 'id'], null)
      const eventId = get(response, ['data', 'event_id'], null)
      const updated = get(response, ['data', 'updated'], null)

      return { checking: true, eventId, id, updated }
    }

    return response.data
  })
}

export async function callFileApiV3({ data, endpoint }) {
  return axios({
    data,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    method: 'PUT',
    url: endpoint,
  })
}

export function isPair(list) {
  if (!isArray(list)) {
    return false
  }

  return list.length === 2
}

export function getNormalizedParams(paramsObj) {
  return Object.entries(paramsObj).filter(isPair)
}

export function isArrayParam(param) {
  return isArray(param[1])
}

export function getSingleFieldParams(paramsObj) {
  const paramsList = getNormalizedParams(paramsObj)
  const params = paramsList.filter(isArrayParam)

  return params
}

export function isApiSuccessStatus(status) {
  return SUCCESS_CODES.indexOf(status) !== -1
}

export function isApiAcceptedStatus(status) {
  return status === HTTP_STATUS_ACCEPTED
}

export function isApiBadRequestStatus(status) {
  return status === HTTP_STATUS_BAD_REQUEST
}

export function isApiUnauthorizedStatus(status) {
  return status === HTTP_STATUS_UNAUTHORIZED
}

export function isApiServerInternalError(status) {
  return status === HTTP_STATUS_INTERNAL_SERVER_ERROR
}

export function isApiForbiddenStatus(status) {
  return status === HTTP_STATUS_FORBIDDEN
}

export function isApiNotFoundStatus(status) {
  return status === HTTP_STATUS_NOT_FOUND
}

export function isApiConflictStatus(status) {
  return status === HTTP_STATUS_CONFLICT
}

export function isApiPaymentRequiredStatus(status) {
  return status === HTTP_STATUS_PAYMENT_REQUIRED
}

export function isApiTooManyRequestsStatus(status) {
  return status === HTTP_STATUS_TOO_MANY_REQUESTS
}

/*
 * Convert object to `URLSearchParams` array.
 */
export function toURLSearchParams(value) {
  return new URLSearchParams(
    Object.entries(value).reduce((acc, [paramName, paramValue]) => {
      const nextValue = Array.isArray(paramValue)
        ? paramValue.map(value => [paramName, value])
        : [[paramName, paramValue]]

      return [...acc, ...nextValue]
    }, [])
  )
}

/**
 * Get items from `/idwell-api/*` response`
 */
export function getItemsFromIdwellApiResponse(response) {
  return response?.data?.items
}

/**
 * Get items from `/api/*` response
 */
export function getItemsFromApiResponse(response) {
  return response?.data?.results?.objects
}

/**
 * Add to request body is_redesign_context: true
 */
export function addRedesignContextApiParam(data) {
  return { ...data, [API_PARAM_IS_REDESIGN_CONTEXT]: true }
}
